import React, { useCallback, useMemo, useState, useEffect } from 'react';
import isHotkey from 'is-hotkey';
import { Editable, withReact, Slate } from 'slate-react';
import { createEditor, Transforms } from 'slate';
import { withHistory } from 'slate-history';

import { toggleMark, withInlines } from './helpers';
import Element from './Element';
import Leaf from './Leaf';
import Toolbar from './Toolbar';
import { Paper } from '@mui/material';
import CharacterCounterSlate from './CharacterCounterSlate';
import EmojiPickerButton from '../emoji-picker';

const HOTKEYS = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
    'mod+`': 'code'
};

const CHARACTER_LIMIT = 340;

const RichText = (props) => {
    const renderElement = useCallback((it) => <Element {...it} />, []);
    const renderLeaf = useCallback((it) => <Leaf {...it} />, []);
    const editor = useMemo(
        () => withInlines(withHistory(withReact(createEditor()))),
        []
    );

    // Function to parse value
    // Check structure validity
    // Check each element
    // Check node children
    const parseValue = useCallback((value) => {
        try {
            if (!value || value === '') {
                return [{ type: 'paragraph', children: [{ text: '' }] }];
            }
            
            const parsed = JSON.parse(value);
            
            // Check structure validity
            if (!Array.isArray(parsed) || parsed.length === 0) {
                return [{ type: 'paragraph', children: [{ text: '' }] }];
            }
            
            // Check each element
            const validatedParsed = parsed.map(node => {
                if (!node || typeof node !== 'object' || !node.children || !Array.isArray(node.children)) {
                    return { type: 'paragraph', children: [{ text: '' }] };
                }
                
                // Check node children
                const validChildren = node.children.map(child => {
                    if (!child || typeof child !== 'object') {
                        return { text: '' };
                    }
                    return { ...child, text: child.text || '' };
                });
                
                return {
                    ...node,
                    type: node.type || 'paragraph',
                    children: validChildren
                };
            });
            
            return validatedParsed;
        } catch (error) {
            return [{ type: 'paragraph', children: [{ text: '' }] }];
        }
    }, []);

    // State for editor value
    const [value, setValue] = useState(() => parseValue(props.value));

    // Function for safe selection restore
    const restoreSelection = useCallback((editorInstance, selection, maxPath) => {
        if (!selection?.anchor?.path || !selection?.focus?.path) return;
        
        try {
            const isValidPath = selection.anchor.path[0] <= maxPath && 
                               selection.focus.path[0] <= maxPath;
            if (isValidPath) {
                editorInstance.selection = selection;
            }
        } catch (selectionError) {
            console.warn('Could not restore selection:', selectionError);
        }
    }, []);

    // Update value when props.value changes
    useEffect(() => {
        try {
            const newValue = parseValue(props.value);
            
            // Check if value actually changed
            // Compare with current editor children, not state
            const currentEditorString = editor?.children ? JSON.stringify(editor.children) : '';
            const newValueString = JSON.stringify(newValue);
            
            if (currentEditorString === newValueString) return;
            
            setValue(newValue);
            
            // Update editor content only if value really changed
            if (!editor?.children || !Array.isArray(newValue)) return;
            
            // Save current selection
            const currentSelection = editor.selection;
            
            // Safely update editor content
            editor.children = newValue;
            
            try {
                editor.normalize({ force: true });
                
                // Restore selection if it was valid
                if (currentSelection) {
                    const maxPath = newValue.length - 1;
                    restoreSelection(editor, currentSelection, maxPath);
                }
                
                editor.onChange();
            } catch (normalizeError) {
                console.warn('Error normalizing editor:', normalizeError);
            }
        } catch (error) {
            console.warn('Error in useEffect for props.value:', error);
        }
    }, [props.value, parseValue, editor, restoreSelection]);

    if (props.disableEditor) {
        return (
            <Slate editor={editor} initialValue={value}>
                <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    readOnly
                />
            </Slate>
        );
    }

    const enableEmojiPicker = props.enableEmojiPicker;

    const handleEmojiClick = (emoji) => {
        if (!editor) return;
        Transforms.insertText(editor, emoji);
    };

    return (
        <div>
            <Paper
                sx={{
                    borderColor: 'divider',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    backgroundColor: 'background.default'
                }}
            >
                <Slate
                    onChange={(data) => {
                        try {
                            // Validate data
                            if (!data || !Array.isArray(data)) {
                                console.warn('Invalid data received in onChange:', data);
                                return;
                            }
                            
                            // Ensure at least one paragraph exists
                            if (data.length === 0) {
                                data = [{ type: 'paragraph', children: [{ text: '' }] }];
                            }
                            
                            // Check if data actually changed
                            const currentDataString = JSON.stringify(value);
                            const newDataString = JSON.stringify(data);
                            
                            if (currentDataString !== newDataString) {
                                setValue(data);
                                props.onChange(newDataString);
                            }
                        } catch (error) {
                            console.warn('Error in Slate onChange:', error);
                            // Use fallback value in case of error
                            const fallbackData = [{ type: 'paragraph', children: [{ text: '' }] }];
                            setValue(fallbackData);
                            props.onChange(JSON.stringify(fallbackData));
                        }
                    }}
                    editor={editor}
                    initialValue={value}
                    value={value}
                >
                    <Toolbar
                        buttons={
                            props.toolbarButtons || [
                                'bold',
                                'italic',
                                'underline',
                                'code'
                            ]
                        }
                        extraButton={
                            enableEmojiPicker ? (
                                <EmojiPickerButton onClick={handleEmojiClick} />
                            ) : null
                        }
                    />
                    <Editable
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        placeholder={props.label}
                        spellCheck
                        autoFocus
                        style={{
                            paddingLeft: '16px',
                            paddingRight: '16px',
                            minHeight: '100px',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: '24px',
                            outline: 'none'
                        }}
                        onKeyDown={(event) => {
                            try {
                                const text = editor.children
                                    .map((n) =>
                                        n.children.map((t) => t.text).join('')
                                    )
                                    .join('');
                                    
                                if (
                                    text.length >= CHARACTER_LIMIT &&
                                    event.key !== 'Backspace' &&
                                    event.key !== 'Delete'
                                ) {
                                    event.preventDefault();
                                    return;
                                }
                                
                                // Handle Enter key
                                if (event.key === 'Enter') {
                                    // Check editor state validity
                                    if (!editor.selection) {
                                        event.preventDefault();
                                        return;
                                    }
                                    
                                    try {
                                        // Allow default Enter handling
                                        // Slate.js will handle node splitting
                                    } catch (enterError) {
                                        console.warn('Error handling Enter key:', enterError);
                                        event.preventDefault();
                                        return;
                                    }
                                    return;
                                }
                                
                                for (const hotkey in HOTKEYS) {
                                    if (isHotkey(hotkey, event)) {
                                        event.preventDefault();
                                        const mark = HOTKEYS[hotkey];
                                        toggleMark(editor, mark);
                                    }
                                }
                            } catch (error) {
                                console.warn('Error in onKeyDown:', error);
                                event.preventDefault();
                            }
                        }}
                    />
                </Slate>
            </Paper>
            <CharacterCounterSlate
                editor={editor}
                characterLimit={CHARACTER_LIMIT}
            />
        </div>
    );
};

export default RichText;
